import {
  Alert,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import * as React from "react";

import { RegistrationMode } from "@/shared/domain/mcp";
import {
  InfoOutlinedIcon,
  LockOutlinedIcon,
} from "@/shared/foundation/icons/mui";
import { Button } from "@/shared/foundation/primitives";

import styles from "./RegisterMcpServerDialog.module.css";

export interface McpServerFormData {
  registration_mode: RegistrationMode;
  server_url: string;
  display_name: string;
  oauth_issuer_url: string;
  authorization_endpoint?: string;
  token_endpoint?: string;
  client_id?: string;
  client_secret?: string;
  revocation_endpoint?: string;
  scopes: string[];
  extra_auth_params?: Record<string, string>;
}

interface RegisterMcpServerDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: McpServerFormData) => void;
  loading?: boolean;
  error?: string;
  /** Optional slot rendered below the form fields (e.g. org autocomplete). */
  footer?: React.ReactNode;
}

async function discoverOAuthMetadata(
  issuerUrl: string,
): Promise<Record<string, unknown> | null> {
  const base = issuerUrl.replace(/\/+$/, "");
  const urls = [
    `${base}/.well-known/oauth-authorization-server`,
    `${base}/.well-known/openid-configuration`,
  ];

  for (const url of urls) {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (response.ok) {
        return (await response.json()) as Record<string, unknown>;
      }
    } catch {
      // CORS blocked or network error — try next
    }
  }
  return null;
}

const DISPLAY_NAME_PATTERN = /^[a-zA-Z0-9_-]+$/;
const DISPLAY_NAME_MAX_LENGTH = 32;
const DISPLAY_NAME_ERROR =
  "Use only letters, numbers, hyphens, or underscores (no spaces). Max 32 characters.";

function displayNameError(value: string): string | null {
  if (!value) {
    return null;
  }
  if (value.length > DISPLAY_NAME_MAX_LENGTH) {
    return DISPLAY_NAME_ERROR;
  }
  if (!DISPLAY_NAME_PATTERN.test(value)) {
    return DISPLAY_NAME_ERROR;
  }
  return null;
}

export function RegisterMcpServerDialog({
  open,
  onClose,
  onSubmit,
  loading,
  error,
  footer,
}: RegisterMcpServerDialogProps) {
  const [mode, setMode] = React.useState<RegistrationMode>("manual");
  const [displayName, setDisplayName] = React.useState("");
  const [serverUrl, setServerUrl] = React.useState("");
  const [oauthIssuerUrl, setOauthIssuerUrl] = React.useState("");
  const [authEndpoint, setAuthEndpoint] = React.useState("");
  const [tokenEndpoint, setTokenEndpoint] = React.useState("");
  const [revocationEndpoint, setRevocationEndpoint] = React.useState("");
  const [clientId, setClientId] = React.useState("");
  const [clientSecret, setClientSecret] = React.useState("");
  const [scopesText, setScopesText] = React.useState("");
  const [extraParamsText, setExtraParamsText] = React.useState("");
  const [discovering, setDiscovering] = React.useState(false);
  const [discoveryStatus, setDiscoveryStatus] = React.useState<
    "idle" | "found" | "not-found"
  >("idle");
  const [helpOpen, setHelpOpen] = React.useState(false);

  const isManual = mode === "manual";

  const handleIssuerBlur = async () => {
    if (!oauthIssuerUrl || !isManual) {
      return;
    }

    setDiscovering(true);
    setDiscoveryStatus("idle");
    const metadata = await discoverOAuthMetadata(oauthIssuerUrl);
    setDiscovering(false);

    if (metadata) {
      setDiscoveryStatus("found");
      if (metadata.authorization_endpoint && !authEndpoint) {
        setAuthEndpoint(metadata.authorization_endpoint as string);
      }
      if (metadata.token_endpoint && !tokenEndpoint) {
        setTokenEndpoint(metadata.token_endpoint as string);
      }
      if (metadata.revocation_endpoint && !revocationEndpoint) {
        setRevocationEndpoint(metadata.revocation_endpoint as string);
      }
    } else {
      setDiscoveryStatus("not-found");
    }
  };

  const handleSubmit = () => {
    const scopes = scopesText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const extraParams: Record<string, string> = {};
    if (extraParamsText.trim()) {
      for (const pair of extraParamsText.split(",")) {
        const [key, ...rest] = pair.split("=");
        if (key?.trim() && rest.length > 0) {
          extraParams[key.trim()] = rest.join("=").trim();
        }
      }
    }

    onSubmit({
      registration_mode: mode,
      server_url: serverUrl,
      display_name: displayName,
      oauth_issuer_url: oauthIssuerUrl,
      ...(isManual
        ? {
            authorization_endpoint: authEndpoint || undefined,
            token_endpoint: tokenEndpoint || undefined,
            client_id: clientId || undefined,
            client_secret: clientSecret || undefined,
            revocation_endpoint: revocationEndpoint || undefined,
          }
        : {}),
      scopes,
      ...(Object.keys(extraParams).length > 0
        ? { extra_auth_params: extraParams }
        : {}),
    });
  };

  const displayNameErr = displayNameError(displayName);
  const isSubmitDisabled = isManual
    ? !serverUrl ||
      !displayName ||
      !!displayNameErr ||
      !clientId ||
      !clientSecret ||
      !authEndpoint ||
      !tokenEndpoint ||
      loading
    : !serverUrl || !displayName || !!displayNameErr || loading;

  // Reset form when dialog closes
  React.useEffect(() => {
    if (!open) {
      setMode("manual");
      setDisplayName("");
      setServerUrl("");
      setOauthIssuerUrl("");
      setAuthEndpoint("");
      setTokenEndpoint("");
      setRevocationEndpoint("");
      setClientId("");
      setClientSecret("");
      setScopesText("");
      setExtraParamsText("");
      setDiscoveryStatus("idle");
      setHelpOpen(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Register MCP Server</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" className={styles.alert}>
            {error}
          </Alert>
        )}

        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_e, v: RegistrationMode | null) => {
            if (v !== null) {
              setMode(v);
            }
          }}
          size="small"
          className={styles.modeToggle}
        >
          <ToggleButton value="dcr">DCR (Auto)</ToggleButton>
          <ToggleButton value="manual">Manual</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          fullWidth
          required
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          error={!!displayNameErr}
          helperText={displayNameErr ?? undefined}
          margin="dense"
          size="small"
        />
        <TextField
          fullWidth
          required
          label="Server URL"
          value={serverUrl}
          onChange={(e) => setServerUrl(e.target.value)}
          margin="dense"
          size="small"
          placeholder="https://api.githubcopilot.com/mcp/"
          helperText="Streamable HTTP endpoint of the MCP server"
        />
        <TextField
          fullWidth
          required={isManual}
          label="OAuth Issuer URL"
          value={oauthIssuerUrl}
          onChange={(e) => setOauthIssuerUrl(e.target.value)}
          onBlur={() => void handleIssuerBlur()}
          margin="dense"
          size="small"
          placeholder="https://github.com"
          helperText={
            isManual
              ? "Endpoints will be auto-discovered from .well-known if available"
              : "Used for DCR metadata discovery"
          }
          InputProps={{
            endAdornment: discovering ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
        />

        {isManual && (
          <>
            {discoveryStatus === "found" && (
              <Alert severity="success" className={styles.discoveryAlert}>
                Endpoints auto-filled from .well-known metadata
              </Alert>
            )}
            {discoveryStatus === "not-found" && (
              <Alert severity="info" className={styles.discoveryAlert}>
                Could not discover endpoints automatically &mdash; fill them in
                manually
              </Alert>
            )}

            <TextField
              fullWidth
              required
              label="Authorization Endpoint"
              value={authEndpoint}
              onChange={(e) => setAuthEndpoint(e.target.value)}
              margin="dense"
              size="small"
              placeholder="https://github.com/login/oauth/authorize"
            />
            <TextField
              fullWidth
              required
              label="Token Endpoint"
              value={tokenEndpoint}
              onChange={(e) => setTokenEndpoint(e.target.value)}
              margin="dense"
              size="small"
              placeholder="https://github.com/login/oauth/access_token"
            />
            <TextField
              fullWidth
              label="Revocation Endpoint"
              value={revocationEndpoint}
              onChange={(e) => setRevocationEndpoint(e.target.value)}
              margin="dense"
              size="small"
            />
            <TextField
              fullWidth
              required
              label="Client ID"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              margin="dense"
              size="small"
              helperText={
                <>
                  From the OAuth app you registered on the provider. Callback
                  URL: <code>{window.location.origin}/oauth/mcp/callback</code>
                </>
              }
            />
            <TextField
              fullWidth
              required
              label="Client Secret"
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              margin="dense"
              size="small"
            />
          </>
        )}

        <TextField
          fullWidth
          label="Scopes"
          value={scopesText}
          onChange={(e) => setScopesText(e.target.value)}
          margin="dense"
          size="small"
          placeholder="repo, read:org"
          helperText="Comma-separated"
        />
        <TextField
          fullWidth
          label="Extra Auth Params"
          value={extraParamsText}
          onChange={(e) => setExtraParamsText(e.target.value)}
          margin="dense"
          size="small"
          placeholder="audience=api.atlassian.com, prompt=consent"
          helperText="key=value pairs, comma-separated (e.g. for Atlassian)"
        />

        {footer}

        {/* Collapsible help section */}
        <div className={styles.helpToggle}>
          <button
            type="button"
            className={styles.helpButton}
            onClick={() => setHelpOpen((prev) => !prev)}
          >
            <InfoOutlinedIcon className={styles.helpIcon} />
            <span>{helpOpen ? "Hide help" : "Need help?"}</span>
          </button>
        </div>
        <Collapse in={helpOpen}>
          <div className={styles.helpContent}>
            <div className={styles.helpSection}>
              <strong>DCR vs Manual</strong>
              <p>
                <strong>DCR</strong> (Dynamic Client Registration) automatically
                negotiates OAuth credentials with the server. Most providers
                don&apos;t support this yet, so <strong>Manual</strong> is the
                default.
              </p>
            </div>
            <div className={styles.helpSection}>
              <strong>Manual setup</strong>
              <p>
                Register an OAuth application on the provider&apos;s platform
                (e.g. GitHub &rarr; Settings &rarr; Developer settings &rarr;
                OAuth Apps). Set the callback URL to{" "}
                <code>{window.location.origin}/oauth/mcp/callback</code>, then
                copy the Client ID and Secret here.
              </p>
            </div>
            <div className={styles.helpSection}>
              <strong>Finding endpoints</strong>
              <p>
                Enter the OAuth Issuer URL and we&apos;ll try to auto-discover
                endpoints from{" "}
                <code>.well-known/oauth-authorization-server</code>. If that
                fails, check the provider&apos;s API documentation.
              </p>
            </div>
            <div className={styles.helpSection}>
              <LockOutlinedIcon className={styles.helpIcon} />
              <strong>Security</strong>
              <p>
                Client secrets and tokens are encrypted at rest with AWS KMS
                envelope encryption. They are never stored in plaintext.
              </p>
            </div>
          </div>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {loading ? "Registering..." : "Register"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
